import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Button, Card, Alert, Spinner, Row, Col, ListGroup, CloseButton } from 'react-bootstrap';
import { getSurvey, updateSurvey } from '../services/api';

const EditSurvey = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [groups, setGroups] = useState([]);
  const [generalQuestions, setGeneralQuestions] = useState([]);
  
  // New group form state
  const [newGroupName, setNewGroupName] = useState('');
  const [selectedParentGroup, setSelectedParentGroup] = useState(null);
  
  // New question form state
  const [newQuestion, setNewQuestion] = useState({
    text: '',
    type: 'text',
    notes: '',
    options: '',
    groupId: null // null for general questions, or group ID
  });

  // Load survey data
  useEffect(() => {
    const fetchSurvey = async () => {
      try {
        setLoading(true);
        const data = await getSurvey(surveyId);
        
        // Set survey data
        setTitle(data.title);
        setDescription(data.description || '');
        setGroups(data.groups || []);
        setGeneralQuestions(data.general_questions || []);
        
        setError(null);
      } catch (err) {
        console.error('Error fetching survey:', err);
        setError('Failed to load survey. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchSurvey();
  }, [surveyId]);

  // Get all groups including nested ones in a flat list for dropdown
  const getAllGroups = (groups, level = 0, parentName = '') => {
    let allGroups = [];
    groups.forEach(group => {
      const displayName = parentName ? `${parentName} > ${group.name}` : group.name;
      allGroups.push({
        ...group,
        displayName,
        level
      });
      if (group.sub_groups) {
        allGroups = allGroups.concat(getAllGroups(group.sub_groups, level + 1, displayName));
      }
    });
    return allGroups;
  };

  const flatGroups = getAllGroups(groups);

  const addGroup = () => {
    if (!newGroupName.trim()) return;
    
    const newGroup = {
      id: Date.now().toString(), // Temporary ID
      name: newGroupName,
      questions: [],
      sub_groups: [],
      parent_group_id: selectedParentGroup
    };

    if (selectedParentGroup) {
      // Add as sub-group
      const updateGroupsRecursively = (groups) => {
        return groups.map(group => {
          if (group.id === selectedParentGroup) {
            return {
              ...group,
              sub_groups: [...(group.sub_groups || []), newGroup]
            };
          } else if (group.sub_groups) {
            return {
              ...group,
              sub_groups: updateGroupsRecursively(group.sub_groups)
            };
          }
          return group;
        });
      };
      setGroups(updateGroupsRecursively(groups));
    } else {
      // Add as top-level group
      setGroups([...groups, newGroup]);
    }
    
    setNewGroupName('');
    setSelectedParentGroup(null);
  };

  const removeGroup = (groupId) => {
    const removeGroupRecursively = (groups) => {
      return groups.filter(group => {
        if (group.id === groupId) {
          return false;
        }
        if (group.sub_groups) {
          group.sub_groups = removeGroupRecursively(group.sub_groups);
        }
        return true;
      });
    };
    setGroups(removeGroupRecursively(groups));
  };

  const updateGroupName = (groupId, newName) => {
    const updateGroupRecursively = (groups) => {
      return groups.map(group => {
        if (group.id === groupId) {
          return { ...group, name: newName };
        }
        if (group.sub_groups) {
          return {
            ...group,
            sub_groups: updateGroupRecursively(group.sub_groups)
          };
        }
        return group;
      });
    };
    setGroups(updateGroupRecursively(groups));
  };

  const addQuestion = () => {
    if (!newQuestion.text.trim()) return;
    
    const questionObj = {
      id: Date.now().toString(), // Temporary ID
      text: newQuestion.text,
      type: newQuestion.type,
      notes: newQuestion.notes,
      options: newQuestion.type !== 'text' ? newQuestion.options.split(',').map(opt => opt.trim()).filter(opt => opt) : []
    };
    
    if (!newQuestion.groupId) {
      // Add to general questions
      setGeneralQuestions([...generalQuestions, questionObj]);
    } else {
      // Add to specific group
      const addQuestionToGroup = (groups) => {
        return groups.map(group => {
          if (group.id === newQuestion.groupId) {
            return {
              ...group,
              questions: [...(group.questions || []), questionObj]
            };
          }
          if (group.sub_groups) {
            return {
              ...group,
              sub_groups: addQuestionToGroup(group.sub_groups)
            };
          }
          return group;
        });
      };
      setGroups(addQuestionToGroup(groups));
    }
    
    // Reset form
    setNewQuestion({
      text: '',
      type: 'text',
      notes: '',
      options: '',
      groupId: newQuestion.groupId
    });
  };

  const removeQuestion = (questionId, groupId = null) => {
    if (!groupId) {
      // Remove from general questions
      setGeneralQuestions(generalQuestions.filter(q => q.id !== questionId));
    } else {
      // Remove from specific group
      const removeQuestionFromGroup = (groups) => {
        return groups.map(group => {
          if (group.id === groupId) {
            return {
              ...group,
              questions: group.questions.filter(q => q.id !== questionId)
            };
          }
          if (group.sub_groups) {
            return {
              ...group,
              sub_groups: removeQuestionFromGroup(group.sub_groups)
            };
          }
          return group;
        });
      };
      setGroups(removeQuestionFromGroup(groups));
    }
  };

  const updateQuestion = (questionId, field, value, groupId = null) => {
    const updateQuestionObj = (question) => {
      if (field === 'options' && typeof value === 'string') {
        return { ...question, [field]: value.split(',').map(opt => opt.trim()).filter(opt => opt) };
      }
      return { ...question, [field]: value };
    };

    if (!groupId) {
      // Update general question
      setGeneralQuestions(generalQuestions.map(q => 
        q.id === questionId ? updateQuestionObj(q) : q
      ));
    } else {
      // Update group question
      const updateQuestionInGroup = (groups) => {
        return groups.map(group => {
          if (group.id === groupId) {
            return {
              ...group,
              questions: group.questions.map(q => 
                q.id === questionId ? updateQuestionObj(q) : q
              )
            };
          }
          if (group.sub_groups) {
            return {
              ...group,
              sub_groups: updateQuestionInGroup(group.sub_groups)
            };
          }
          return group;
        });
      };
      setGroups(updateQuestionInGroup(groups));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim()) {
      setError('Survey title is required');
      return;
    }
    
    try {
      setSubmitting(true);
      setError(null);
      
      const surveyData = {
        title,
        description,
        groups,
        general_questions: generalQuestions
      };
      
      await updateSurvey(surveyId, surveyData);
      
      setSuccessMessage('Survey updated successfully!');
      
      // Navigate back to survey detail page after successful update
      setTimeout(() => {
        navigate(`/surveys/${surveyId}`);
      }, 1500);
      
    } catch (err) {
      console.error('Error updating survey:', err);
      setError('Failed to update survey. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderGroup = (group, level = 0) => (
    <Card key={group.id} className={`mb-3`} style={{ marginLeft: `${level * 20}px` }}>
      <Card.Header className={`d-flex justify-content-between align-items-center ${level > 0 ? 'bg-light' : ''}`}>
        <Form.Control
          type="text"
          value={group.name}
          onChange={(e) => updateGroupName(group.id, e.target.value)}
          placeholder="Group name"
          className="me-2 border-0"
          style={{ backgroundColor: 'transparent' }}
        />
        <CloseButton onClick={() => removeGroup(group.id)} />
      </Card.Header>
      <Card.Body>
        <div className="mb-3">
          <Button 
            variant="outline-primary" 
            size="sm"
            onClick={() => setNewQuestion({...newQuestion, groupId: group.id})}
            className="mb-3"
          >
            Add Question to This Group
          </Button>
          
          {group.questions && group.questions.length > 0 ? (
            <ListGroup variant="flush">
              {group.questions.map((question) => (
                <ListGroup.Item key={question.id}>
                  <div className="mb-2 d-flex justify-content-between align-items-center">
                    <Form.Control
                      type="text"
                      value={question.text}
                      onChange={(e) => updateQuestion(question.id, 'text', e.target.value, group.id)}
                      placeholder="Question text"
                    />
                    <CloseButton onClick={() => removeQuestion(question.id, group.id)} />
                  </div>
                  <Row className="g-2 mb-2">
                    <Col md={4}>
                      <Form.Select
                        value={question.type}
                        onChange={(e) => updateQuestion(question.id, 'type', e.target.value, group.id)}
                      >
                        <option value="text">Text</option>
                        <option value="select">Select (dropdown)</option>
                        <option value="checkbox">Checkbox (multi-select)</option>
                        <option value="radio">Radio (single choice)</option>
                      </Form.Select>
                    </Col>
                    <Col md={8}>
                      {question.type !== 'text' && (
                        <Form.Control
                          type="text"
                          value={question.options ? question.options.join(', ') : ''}
                          onChange={(e) => updateQuestion(question.id, 'options', e.target.value, group.id)}
                          placeholder="Options (comma-separated)"
                        />
                      )}
                    </Col>
                  </Row>
                  <Form.Control
                    type="text"
                    value={question.notes || ''}
                    onChange={(e) => updateQuestion(question.id, 'notes', e.target.value, group.id)}
                    placeholder="Notes (optional)"
                    size="sm"
                  />
                </ListGroup.Item>
              ))}
            </ListGroup>
          ) : (
            <div className="text-center py-3 text-muted">
              No questions in this group yet
            </div>
          )}
        </div>
        
        {/* Render sub-groups */}
        {group.sub_groups && group.sub_groups.map(subGroup => 
          renderGroup(subGroup, level + 1)
        )}
      </Card.Body>
    </Card>
  );

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div>
      <h1 className="mb-4">Edit Survey</h1>
      
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {successMessage && (
        <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}
      
      <Alert variant="warning" className="mb-4">
        <Alert.Heading>Nested Groups Support</Alert.Heading>
        <p>This survey editor now supports nested groups! You can create sub-groups within groups for better organization.</p>
        <p className="mb-0"><strong>Note:</strong> When updating an existing survey, group and question IDs may change. This is expected behavior.</p>
      </Alert>
      
      <Form onSubmit={handleSubmit}>
        <Card className="mb-4">
          <Card.Header>
            <h2 className="h5 mb-0">Survey Details</h2>
          </Card.Header>
          <Card.Body>
            <Form.Group className="mb-3">
              <Form.Label>Title *</Form.Label>
              <Form.Control
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter survey title"
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter survey description (optional)"
              />
            </Form.Group>
          </Card.Body>
        </Card>
        
        <Card className="mb-4">
          <Card.Header>
            <h2 className="h5 mb-0">Question Groups</h2>
          </Card.Header>
          <Card.Body>
            <Form.Group className="mb-3">
              <Form.Label>Add New Group</Form.Label>
              <Row>
                <Col md={6}>
                  <Form.Control
                    type="text"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    placeholder="Enter group name (e.g., Personal Information)"
                    className="mb-2"
                  />
                </Col>
                <Col md={4}>
                  <Form.Select
                    value={selectedParentGroup || ''}
                    onChange={(e) => setSelectedParentGroup(e.target.value || null)}
                    className="mb-2"
                  >
                    <option value="">Top Level (No Parent)</option>
                    {flatGroups.map(group => (
                      <option key={group.id} value={group.id}>
                        {'  '.repeat(group.level) + group.displayName}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Button variant="outline-primary" onClick={addGroup} className="w-100">
                    Add Group
                  </Button>
                </Col>
              </Row>
            </Form.Group>
            
            {groups.length > 0 && (
              <div className="mb-4">
                {groups.map(group => renderGroup(group))}
              </div>
            )}
          </Card.Body>
        </Card>
        
        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h2 className="h5 mb-0">General Questions</h2>
            {generalQuestions.length > 0 && (
              <span className="badge bg-secondary">{generalQuestions.length} questions</span>
            )}
          </Card.Header>
          <Card.Body>
            <Card className="mb-4">
              <Card.Header>
                <h3 className="h6 mb-0">Add New Question</h3>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Question Text *</Form.Label>
                  <Form.Control
                    type="text"
                    value={newQuestion.text}
                    onChange={(e) => setNewQuestion({...newQuestion, text: e.target.value})}
                    placeholder="Enter question text"
                  />
                </Form.Group>
                
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Question Type</Form.Label>
                      <Form.Select
                        value={newQuestion.type}
                        onChange={(e) => setNewQuestion({...newQuestion, type: e.target.value})}
                      >
                        <option value="text">Text</option>
                        <option value="select">Select (dropdown)</option>
                        <option value="checkbox">Checkbox (multi-select)</option>
                        <option value="radio">Radio (single choice)</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Add to Group</Form.Label>
                      <Form.Select
                        value={newQuestion.groupId || ''}
                        onChange={(e) => setNewQuestion({...newQuestion, groupId: e.target.value || null})}
                      >
                        <option value="">General Questions (No Group)</option>
                        {flatGroups.map(group => (
                          <option key={group.id} value={group.id}>
                            {'  '.repeat(group.level) + group.displayName}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                
                {newQuestion.type !== 'text' && (
                  <Form.Group className="mb-3">
                    <Form.Label>Options (comma-separated)</Form.Label>
                    <Form.Control
                      type="text"
                      value={newQuestion.options}
                      onChange={(e) => setNewQuestion({...newQuestion, options: e.target.value})}
                      placeholder="Enter options separated by commas (e.g., Yes, No, Maybe)"
                    />
                    <Form.Text className="text-muted">
                      For multiple choice questions, enter options separated by commas
                    </Form.Text>
                  </Form.Group>
                )}
                
                <Form.Group className="mb-3">
                  <Form.Label>Notes (optional)</Form.Label>
                  <Form.Control
                    type="text"
                    value={newQuestion.notes}
                    onChange={(e) => setNewQuestion({...newQuestion, notes: e.target.value})}
                    placeholder="Additional information about this question"
                  />
                </Form.Group>
                
                <div className="text-end">
                  <Button variant="primary" onClick={addQuestion}>
                    Add Question
                  </Button>
                </div>
              </Card.Body>
            </Card>
            
            {generalQuestions.length > 0 && (
              <div>
                <h3 className="h6 mb-3">General Questions</h3>
                <ListGroup variant="flush">
                  {generalQuestions.map((question) => (
                    <ListGroup.Item key={question.id}>
                      <div className="mb-2 d-flex justify-content-between align-items-center">
                        <Form.Control
                          type="text"
                          value={question.text}
                          onChange={(e) => updateQuestion(question.id, 'text', e.target.value)}
                          placeholder="Question text"
                        />
                        <CloseButton onClick={() => removeQuestion(question.id)} />
                      </div>
                      <Row className="g-2 mb-2">
                        <Col md={4}>
                          <Form.Select
                            value={question.type}
                            onChange={(e) => updateQuestion(question.id, 'type', e.target.value)}
                          >
                            <option value="text">Text</option>
                            <option value="select">Select (dropdown)</option>
                            <option value="checkbox">Checkbox (multi-select)</option>
                            <option value="radio">Radio (single choice)</option>
                          </Form.Select>
                        </Col>
                        <Col md={8}>
                          {question.type !== 'text' && (
                            <Form.Control
                              type="text"
                              value={question.options ? question.options.join(', ') : ''}
                              onChange={(e) => updateQuestion(question.id, 'options', e.target.value)}
                              placeholder="Options (comma-separated)"
                            />
                          )}
                        </Col>
                      </Row>
                      <Form.Control
                        type="text"
                        value={question.notes || ''}
                        onChange={(e) => updateQuestion(question.id, 'notes', e.target.value)}
                        placeholder="Notes (optional)"
                        size="sm"
                      />
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </div>
            )}
          </Card.Body>
        </Card>
        
        <div className="d-flex justify-content-between">
          <Button variant="outline-secondary" onClick={() => navigate(`/surveys/${surveyId}`)}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={submitting}>
            {submitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default EditSurvey;